// Dashboard › Atividade da equipe (specs/16): o que cada vendedor fez no dia. Só administrador.
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, NavLink, useSearchParams } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon } from '../components/ui';
import { moeda, num } from '../format';
import { ContatosEquipe } from './ContatosEquipe';
import { ResumoStatus } from './ResumoStatus';

type Periodo = 'hoje' | 'ontem' | '7d';
interface Vendedor {
  id: string; nome: string; perfil: 'ADMIN' | 'VENDEDOR'; ultimoAcesso: string | null; metaDia: number; metaPropria: boolean;
  contatos: number; whatsapp: number; email: number; ligacao: number; visita: number; clientesUnicos: number;
  primeiraAcao: string | null; ultimaAcao: string | null; retornosFeitos: number; retornosAtrasados: number;
  novosClientes: number; oportunidades: number; valorOportunidades: string; vendas: number; valorVendas: string; respostasEmail: number;
}
interface Pendente { id: string; usuarioId: string; clienteId: string; cliente: string; assunto: string; recebidoEm: string }
interface Dados {
  periodo: Periodo; dia: string; dias: number; metaContatosDia: number;
  totais: { clientesUnicos: number; mediaContatos: number; mediaClientesUnicos: number };
  vendedores: Vendedor[]; ritmo: Array<{ usuarioId: string; hora: number; n: number }>; respostasPendentes: Pendente[];
}
interface Atividade {
  id: string; tipo: string; descricao: string; createdAt: string; clienteId: string | null; cliente: string | null;
  campanhaId: string | null; campanha: string | null; dados: { assunto?: string; trecho?: string } & Record<string, unknown>;
}

/** Abas do Dashboard (a de equipe só para quem vê a empresa toda). */
export function AbasDashboard() {
  const { pode } = useAuth();
  if (!pode('RELATORIOS_TENANT')) return null;
  return (
    <nav className="tabs" aria-label="Visões do dashboard">
      <NavLink to="/" end className="tab">Visão geral</NavLink>
      <NavLink to="/equipe" className="tab">Atividade da equipe</NavLink>
    </nav>
  );
}

const hora = (ts: string | null) => (ts ? new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—');
const diaHora = (ts: string) => new Date(ts).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const iniciais = (s: string) => s.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
const milhar = (v: number) => (v >= 1000 ? `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil` : moeda(v));
const minutosDesde = (ts: string | null) => (ts ? (Date.now() - new Date(ts).getTime()) / 60_000 : Infinity);
function variacao(atual: number, media: number) {
  if (!media) return null;
  const pct = Math.round(((atual - media) / media) * 100);
  return { pct, texto: `${pct >= 0 ? '+' : ''}${pct}% vs. média (${num(Math.round(media))})` };
}

const ROTULO_PERIODO: Record<Periodo, string> = { hoje: 'Hoje', ontem: 'Ontem', '7d': 'Últimos 7 dias' };

export function DashboardEquipePage() {
  const { pode } = useAuth();
  const [periodo, setPeriodo] = useState<Periodo>('hoje');
  const [sel, setSel] = useState<string | null>(null);
  // visão: resumo por vendedor (padrão) ou contatos detalhados (?visao=contatos&vendedor=<id>)
  const [sp, setSp] = useSearchParams();
  const visao = sp.get('visao') === 'contatos' ? 'contatos' : sp.get('visao') === 'status' ? 'status' : 'resumo';
  const irPara = (v: 'resumo' | 'contatos' | 'status', vendedor?: string) => {
    const n = new URLSearchParams(sp);
    if (v !== 'resumo') n.set('visao', v); else n.delete('visao');
    if (vendedor) n.set('vendedor', vendedor); else n.delete('vendedor');
    setSp(n);
  };
  const q = useQuery({
    queryKey: ['equipe', periodo],
    queryFn: () => api<Dados>(`/dashboard/equipe?periodo=${periodo}`),
    enabled: pode('RELATORIOS_TENANT'),
    refetchInterval: periodo === 'hoje' ? 60_000 : false,
  });
  if (!pode('RELATORIOS_TENANT')) return <Erro erro="Só administradores veem a atividade da equipe." />;

  const d = q.data;
  const hoje = periodo === 'hoje';
  const dias = d?.dias ?? 1;
  const metaDe = (v: Vendedor) => v.metaDia * dias;
  const vs = d?.vendedores ?? [];
  const soma = (k: keyof Vendedor) => vs.reduce((s, v) => s + Number(v[k] ?? 0), 0);
  const comMeta = vs.some((v) => v.perfil === 'VENDEDOR') ? vs.filter((v) => v.perfil === 'VENDEDOR') : vs;
  const metaEquipe = comMeta.reduce((s, v) => s + metaDe(v), 0);
  const contatos = soma('contatos');
  const vContatos = d ? variacao(contatos, d.totais.mediaContatos) : null;
  const vUnicos = d ? variacao(d.totais.clientesUnicos, d.totais.mediaClientesUnicos) : null;
  const pendentesPor = new Map<string, Pendente[]>();
  for (const p of d?.respostasPendentes ?? []) pendentesPor.set(p.usuarioId, [...(pendentesPor.get(p.usuarioId) ?? []), p]);

  // ------------------------------------------------------------- alertas
  const alertas: Array<{ grave: boolean; titulo: string; texto: string; usuarioId: string }> = [];
  for (const v of vs) {
    if (v.perfil !== 'VENDEDOR') continue;
    const meta = metaDe(v);
    const pct = meta ? v.contatos / meta : 1;
    if (hoje && !v.ultimaAcao) {
      alertas.push({ grave: true, usuarioId: v.id, titulo: `${v.nome} não registrou nada hoje`,
        texto: `${v.ultimoAcesso ? `Último acesso ${diaHora(v.ultimoAcesso)}` : 'Nunca acessou'}${v.retornosAtrasados ? ` · ${v.retornosAtrasados} retornos atrasados` : ''}.` });
    } else if (hoje && minutosDesde(v.ultimaAcao) > 120 && new Date().getHours() < 18 && pct < 0.5) {
      alertas.push({ grave: false, usuarioId: v.id, titulo: `${v.nome} parou às ${hora(v.ultimaAcao)}`,
        texto: `${v.contatos} contatos (${Math.round(pct * 100)}% da meta)${v.retornosAtrasados ? ` · ${v.retornosAtrasados} retornos atrasados` : ''}.` });
    } else if (v.retornosAtrasados >= 3) {
      alertas.push({ grave: false, usuarioId: v.id, titulo: `${v.nome} tem ${v.retornosAtrasados} retornos atrasados`, texto: 'Clientes esperando o contato combinado.' });
    }
  }
  for (const [uid, ps] of pendentesPor) {
    const v = vs.find((x) => x.id === uid);
    alertas.push({ grave: false, usuarioId: uid,
      titulo: `${ps.length} ${ps.length === 1 ? 'resposta de e-mail esperando' : 'respostas de e-mail esperando'}${v ? ` · ${v.nome}` : ''}`,
      texto: `${ps.slice(0, 2).map((p) => `${p.cliente} (${diaHora(p.recebidoEm)})`).join(', ')}${ps.length > 2 ? '…' : ''} — sem retorno há mais de 4 h.` });
  }

  // ------------------------------------------------------------- ritmo por hora
  const horas = d?.ritmo.map((r) => r.hora) ?? [];
  const hIni = Math.min(8, ...horas);
  const hFim = Math.max(18, ...horas);
  const faixa = Array.from({ length: hFim - hIni + 1 }, (_, i) => hIni + i);
  const maxHora = Math.max(1, ...(d?.ritmo.map((r) => r.n) ?? [1]));
  const porHora = (uid: string, h: number) => d?.ritmo.find((r) => r.usuarioId === uid && r.hora === h)?.n ?? 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <span className="muted small">
            {d ? (periodo === '7d' ? 'Últimos 7 dias' : new Date(`${d.dia}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })) : ' '}
            {hoje && q.dataUpdatedAt ? ` · atualizado às ${new Date(q.dataUpdatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
        </div>
        <div className="actions">
          <div className="seg" role="group" aria-label="Período">
            {(['hoje', 'ontem', '7d'] as const).map((p) => <button key={p} type="button" aria-pressed={periodo === p} onClick={() => setPeriodo(p)}>{ROTULO_PERIODO[p]}</button>)}
          </div>
        </div>
      </div>
      <AbasDashboard />
      <div className="seg" role="tablist" aria-label="Visão" style={{ alignSelf: 'flex-start' }}>
        <button type="button" role="tab" aria-selected={visao === 'resumo'} aria-pressed={visao === 'resumo'} onClick={() => irPara('resumo')}>Resumo por vendedor</button>
        <button type="button" role="tab" aria-selected={visao === 'contatos'} aria-pressed={visao === 'contatos'} onClick={() => irPara('contatos')}>Contatos detalhados</button>
        <button type="button" role="tab" aria-selected={visao === 'status'} aria-pressed={visao === 'status'} onClick={() => irPara('status')}>Resumo por status</button>
      </div>

      {visao === 'contatos' ? (
        <ContatosEquipe periodo={periodo} vendedores={(d?.vendedores ?? []).map((v) => ({ id: v.id, nome: v.nome }))} vendedorInicial={sp.get('vendedor')} />
      ) : visao === 'status' ? (
        <ResumoStatus periodo={periodo} vendedores={(d?.vendedores ?? []).map((v) => ({ id: v.id, nome: v.nome }))} />
      ) : q.isLoading ? <Carregando /> : q.error ? <Erro erro={mensagemErro(q.error)} /> : d && (
        <>
          <div className="grid-kpi" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <Kpi label="Contatos da equipe" valor={num(contatos)}
              sub={`${metaEquipe ? `${Math.round((contatos / metaEquipe) * 100)}% da meta (${num(metaEquipe)})` : ''}${vContatos ? ` · ${vContatos.texto}` : ''}`}
              cor={metaEquipe && contatos >= metaEquipe ? 'green' : vContatos && vContatos.pct < -10 ? 'red' : undefined} />
            <Kpi label="Clientes contatados" valor={num(d.totais.clientesUnicos)} sub={vUnicos?.texto} cor={vUnicos && vUnicos.pct < -10 ? 'red' : vUnicos && vUnicos.pct > 0 ? 'green' : undefined} />
            <Kpi label="Respostas por e-mail" valor={num(soma('respostasEmail'))}
              sub={d.respostasPendentes.length ? `${d.respostasPendentes.length} sem retorno há mais de 4 h` : 'nenhuma esperando'} cor={d.respostasPendentes.length ? 'red' : undefined} />
            <Kpi label="Retornos concluídos" valor={num(soma('retornosFeitos'))}
              sub={`${num(soma('retornosAtrasados'))} atrasados agora`} cor={soma('retornosAtrasados') ? 'red' : undefined} />
            <Kpi label="Novos clientes" valor={num(soma('novosClientes'))} sub={`${num(soma('oportunidades'))} oportunidades · ${milhar(soma('valorOportunidades'))}`} />
            <Kpi label="Vendas" valor={milhar(soma('valorVendas'))} sub={`${num(soma('vendas'))} ganhas`} cor={soma('vendas') ? 'green' : undefined} />
          </div>

          {alertas.length > 0 && (
            <section className="card" aria-labelledby="h-atencao">
              <div className="card-head"><h2 id="h-atencao">Precisa de atenção</h2><span className="badge amber">{alertas.length}</span></div>
              <div className="alertas-eq">
                {alertas.slice(0, 6).map((a, i) => (
                  <div key={i} className={`alerta-eq${a.grave ? ' grave' : ''}`}>
                    <Icon name="alerta" />
                    <div className="col" style={{ gap: 2, flex: 1 }}>
                      <strong style={{ fontSize: 13.5, fontWeight: 600 }}>{a.titulo}</strong>
                      <span className="small muted">{a.texto}</span>
                    </div>
                    <button className="btn sm" onClick={() => setSel(a.usuarioId)}>Ver detalhes</button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="card flush" aria-labelledby="h-vend">
            <div className="card-head" style={{ padding: '16px 18px 12px', flexWrap: 'wrap' }}>
              <h2 id="h-vend">Por vendedor</h2>
              <span className="small faint">Meta da empresa: {d.metaContatosDia} contatos por dia{vs.some((v) => v.metaPropria) ? ' · alguns vendedores têm meta própria' : ''}{pode('CONFIGURACOES_GERENCIAR') && <> · <Link to="/configuracoes/metas">alterar</Link></>}</span>
            </div>
            <div className="table-wrap">
              <table className="tbl-eq">
                <thead>
                  <tr>
                    <th>Vendedor</th><th>{hoje || periodo === 'ontem' ? 'Expediente no CRM' : 'Última ação'}</th><th>Contatos × meta</th>
                    <th className="n">WhatsApp</th><th className="n">E-mail</th><th className="n">Ligação / visita</th><th className="n">Clientes únicos</th>
                    <th className="n">Retornos feitos</th><th className="n">Atrasados</th><th className="n">Novos clientes</th><th className="n">Oportunidades</th><th className="n">Vendas</th>
                  </tr>
                </thead>
                <tbody>
                  {vs.map((v) => {
                    const meta = metaDe(v);
                    const pct = meta ? Math.min(100, Math.round((v.contatos / meta) * 100)) : 0;
                    const recente = minutosDesde(v.ultimaAcao) < 15;
                    const status = !v.ultimaAcao
                      ? (v.ultimoAcesso ? `último acesso ${diaHora(v.ultimoAcesso)}` : 'nunca acessou')
                      : hoje ? (recente ? 'ativo agora' : `última ação ${hora(v.ultimaAcao)}`) : `${v.perfil === 'ADMIN' ? 'administrador' : 'vendedor'}`;
                    return (
                      <tr key={v.id} className={sel === v.id ? 'sel' : undefined}>
                        <td>
                          <button type="button" className="link-btn" style={{ display: 'flex', gap: 10, alignItems: 'center' }} onClick={() => setSel(v.id)}>
                            <span className="av">{iniciais(v.nome)}<span className="dot" style={{ background: !v.ultimaAcao ? 'var(--red-text)' : recente ? '#22c55e' : hoje && pct < 50 ? 'var(--amber)' : '#22c55e' }} /></span>
                            <span className="duas-linhas">{v.nome}<span className="small faint" style={{ fontWeight: 400 }}>{status}</span></span>
                          </button>
                        </td>
                        <td className="num small">{periodo === '7d' ? (v.ultimaAcao ? diaHora(v.ultimaAcao) : '—') : v.primeiraAcao ? `${hora(v.primeiraAcao)} – ${hora(v.ultimaAcao)}` : '—'}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div className="bar" style={{ width: 90 }}><div style={{ width: `${pct}%`, background: pct >= 100 ? '#22c55e' : pct >= 60 ? 'var(--blue)' : 'var(--amber)' }} /></div>
                            <span className="num small" title={v.metaPropria ? 'Meta própria deste vendedor' : 'Meta da empresa'}>{v.contatos} / {meta}{v.metaPropria ? '*' : ''}</span>
                          </div>
                        </td>
                        <td className="n num">{v.whatsapp}</td>
                        <td className="n num">{v.email}</td>
                        <td className="n num">{v.ligacao + v.visita}</td>
                        <td className="n num">{v.clientesUnicos}</td>
                        <td className="n num">{v.retornosFeitos}</td>
                        <td className="n"><span className={`badge${v.retornosAtrasados >= 3 ? ' red' : v.retornosAtrasados ? ' amber' : ''}`}>{v.retornosAtrasados}</span></td>
                        <td className="n num">{v.novosClientes}</td>
                        <td className="n num">{v.oportunidades ? `${v.oportunidades} · ${milhar(Number(v.valorOportunidades))}` : '0'}</td>
                        <td className="n num">{v.vendas ? `${v.vendas} · ${milhar(Number(v.valorVendas))}` : '0'}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ fontWeight: 600 }}>Equipe</td><td />
                    <td className="num small">{num(contatos)} / {num(metaEquipe)}</td>
                    <td className="n num">{num(soma('whatsapp'))}</td><td className="n num">{num(soma('email'))}</td>
                    <td className="n num">{num(soma('ligacao') + soma('visita'))}</td><td className="n num">{num(d.totais.clientesUnicos)}</td>
                    <td className="n num">{num(soma('retornosFeitos'))}</td><td className="n num">{num(soma('retornosAtrasados'))}</td>
                    <td className="n num">{num(soma('novosClientes'))}</td>
                    <td className="n num">{soma('oportunidades')} · {milhar(soma('valorOportunidades'))}</td>
                    <td className="n num">{soma('vendas')} · {milhar(soma('valorVendas'))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          <section className="card" aria-labelledby="h-ritmo">
            <div className="card-head" style={{ flexWrap: 'wrap' }}>
              <h2 id="h-ritmo">Ritmo do dia</h2>
              <span className="small faint">contatos por hora{periodo === '7d' ? ' (soma dos 7 dias)' : ''} — mostra quem parou e quando</span>
            </div>
            <div className="table-wrap">
              <div className="ritmo" style={{ gridTemplateColumns: `150px repeat(${faixa.length}, minmax(22px, 1fr)) 48px` }}>
                {vs.map((v) => (
                  <div key={v.id} className="ritmo-linha" role="img" aria-label={`${v.nome}: ${faixa.map((h) => `${h}h ${porHora(v.id, h)}`).join(', ')}`}>
                    <span className="small" style={{ alignSelf: 'center' }}>{v.nome}</span>
                    {faixa.map((h) => {
                      const n = porHora(v.id, h);
                      return <div key={h} className={`ritmo-col${n ? '' : ' vazio'}`} title={`${h}h: ${n} contatos`}><div style={{ height: Math.max(2, Math.round((n / maxHora) * 52)) }} /></div>;
                    })}
                    <span className="num small muted" style={{ alignSelf: 'center', textAlign: 'right' }}>{v.contatos}</span>
                  </div>
                ))}
                <div className="ritmo-linha">
                  <span />
                  {faixa.map((h) => <span key={h} className="num small faint" style={{ textAlign: 'center' }}>{h}h</span>)}
                  <span />
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {sel && d && visao === 'resumo' && <PainelVendedor vendedor={d.vendedores.find((v) => v.id === sel)!} periodo={periodo} meta={metaDe(d.vendedores.find((v) => v.id === sel)!)} onClose={() => setSel(null)}
        onDetalhar={() => { const id = sel; setSel(null); irPara('contatos', id); }} />}
    </>
  );
}

function Kpi({ label, valor, sub, cor }: { label: string; valor: string; sub?: string; cor?: 'green' | 'red' }) {
  return (
    <div className="card kpi">
      <span className="label">{label}</span>
      <span className="value">{valor}</span>
      {sub && <span className="small" style={{ color: cor === 'red' ? 'var(--red-text)' : cor === 'green' ? 'var(--green-text)' : 'var(--muted)' }}>{sub}</span>}
    </div>
  );
}

// ----------------------------------------------------------------------------- painel do vendedor

const TIPOS: Record<string, { titulo: string; grupo: 'contatos' | 'retornos' | 'clientes' | 'vendas' | 'outros'; icone: string; cls: string }> = {
  WHATSAPP_ENVIADO: { titulo: 'WhatsApp enviado', grupo: 'contatos', icone: 'whatsapp', cls: 'wa' },
  EMAIL_ENVIADO: { titulo: 'E-mail enviado', grupo: 'contatos', icone: 'email', cls: 'mail' },
  EMAIL_RECEBIDO: { titulo: 'Cliente respondeu por e-mail', grupo: 'contatos', icone: 'responder', cls: 'resp' },
  WHATSAPP_CLICADO: { titulo: 'Cliente clicou no WhatsApp do e-mail', grupo: 'contatos', icone: 'whatsapp', cls: 'resp' },
  CONTATO_REGISTRADO: { titulo: 'Contato registrado', grupo: 'contatos', icone: 'telefone', cls: '' },
  ITEM_PULADO: { titulo: 'Pulado na campanha', grupo: 'contatos', icone: 'campanhas', cls: '' },
  TAREFA_CONCLUIDA: { titulo: 'Retorno concluído', grupo: 'retornos', icone: 'check', cls: 'ret' },
  TAREFA_CRIADA: { titulo: 'Retorno agendado', grupo: 'retornos', icone: 'agenda', cls: 'ret' },
  CLIENTE_CRIADO: { titulo: 'Cliente cadastrado', grupo: 'clientes', icone: 'clientes', cls: 'cli' },
  CLIENTE_ATUALIZADO: { titulo: 'Cliente atualizado', grupo: 'clientes', icone: 'clientes', cls: '' },
  CLIENTE_ATRIBUIDO: { titulo: 'Cliente atribuído', grupo: 'clientes', icone: 'clientes', cls: '' },
  CLIENTE_NAO_CONTACTAR: { titulo: 'Marcado como não contactar', grupo: 'clientes', icone: 'alerta', cls: '' },
  NOTA_ADICIONADA: { titulo: 'Nota', grupo: 'clientes', icone: 'lista', cls: '' },
  OPORTUNIDADE_CRIADA: { titulo: 'Oportunidade criada', grupo: 'vendas', icone: 'dinheiro', cls: 'op' },
  VENDA_REGISTRADA: { titulo: 'Venda registrada', grupo: 'vendas', icone: 'dinheiro', cls: 'op' },
  NEGOCIACAO_ETAPA: { titulo: 'Negociação', grupo: 'vendas', icone: 'dinheiro', cls: 'op' },
  NEGOCIACAO_PERDIDA: { titulo: 'Negociação perdida', grupo: 'vendas', icone: 'dinheiro', cls: '' },
};
const tipoDe = (t: string) => TIPOS[t] ?? { titulo: '', grupo: 'outros' as const, icone: 'lista', cls: '' };

interface Linha { a: Atividade; n: number }
/** Junta ações seguidas do mesmo tipo e campanha (ex.: 12 WhatsApps de um lote) numa linha só. */
function agrupar(lista: Atividade[]): Linha[] {
  const out: Linha[] = [];
  for (const a of lista) {
    const ult = out[out.length - 1];
    const prospeccao = a.tipo === 'CLIENTE_CRIADO' && /prospec/i.test(a.descricao);
    const ultProsp = ult && ult.a.tipo === 'CLIENTE_CRIADO' && /prospec/i.test(ult.a.descricao);
    const juntavel = ['WHATSAPP_ENVIADO', 'ITEM_PULADO', 'CLIENTE_CRIADO', 'EMAIL_ENVIADO', 'TAREFA_CONCLUIDA'].includes(a.tipo);
    if (ult && juntavel && ult.a.tipo === a.tipo && (ult.a.campanhaId ?? '') === (a.campanhaId ?? '') && prospeccao === ultProsp
      && Math.abs(new Date(ult.a.createdAt).getTime() - new Date(a.createdAt).getTime()) < 60 * 60_000) {
      ult.n++;
      continue;
    }
    out.push({ a, n: 1 });
  }
  return out;
}

function PainelVendedor({ vendedor: v, periodo, meta, onClose, onDetalhar }: { vendedor: Vendedor; periodo: Periodo; meta: number; onClose: () => void; onDetalhar: () => void }) {
  const [filtro, setFiltro] = useState<'tudo' | 'contatos' | 'retornos' | 'clientes' | 'vendas'>('tudo');
  const q = useQuery({ queryKey: ['equipe-feed', v.id, periodo], queryFn: () => api<Atividade[]>(`/dashboard/equipe/${v.id}/atividades?periodo=${periodo}`) });
  const linhas = agrupar((q.data ?? []).filter((a) => filtro === 'tudo' || tipoDe(a.tipo).grupo === filtro));

  return (
    <div className="overlay drawer-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={`Atividades de ${v.nome}`} style={{ maxWidth: 540 }}>
        <header className="drawer-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="av" style={{ width: 40, height: 40, borderRadius: 20, fontSize: 13 }}>{iniciais(v.nome)}</span>
            <div className="col" style={{ gap: 0, minWidth: 0 }}>
              <h2 style={{ fontSize: 18 }}>{v.nome}</h2>
              <span className="small muted">{ROTULO_PERIODO[periodo]}{v.primeiraAcao ? ` · ${hora(v.primeiraAcao)} – ${hora(v.ultimaAcao)}` : ' · sem atividade'}</span>
            </div>
            <button className="btn sm icon-btn" style={{ marginLeft: 'auto' }} aria-label="Fechar" onClick={onClose}><Icon name="fechar" size={14} /></button>
          </div>
          <div className="mini-grid">
            <div className="mini"><span className="l">Contatos</span><strong className="num">{v.contatos} / {meta}</strong></div>
            <div className="mini"><span className="l">Clientes únicos</span><strong className="num">{v.clientesUnicos}</strong></div>
            <div className="mini"><span className="l">Retornos</span><strong className="num">{v.retornosFeitos} · {v.retornosAtrasados} atr.</strong></div>
            <div className="mini"><span className="l">Vendas</span><strong className="num">{v.vendas ? milhar(Number(v.valorVendas)) : '0'}</strong></div>
          </div>
          <button type="button" className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={onDetalhar}><Icon name="lista" size={14} />Ver contatos detalhados</button>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="group" aria-label="Filtrar">
            {([['tudo', 'Tudo'], ['contatos', 'Contatos'], ['retornos', 'Retornos'], ['clientes', 'Clientes'], ['vendas', 'Vendas']] as const).map(([k, l]) => (
              <button key={k} type="button" className={`pill${filtro === k ? ' on' : ''}`} aria-pressed={filtro === k} onClick={() => setFiltro(k)}>{l}</button>
            ))}
          </div>
        </header>
        <div className="drawer-body" style={{ gap: 0, paddingTop: 4 }}>
          {q.isLoading ? <Carregando /> : q.error ? <Erro erro={mensagemErro(q.error)} /> : !linhas.length ? (
            <div className="empty">{q.data?.length ? 'Nada deste tipo no período.' : `Nenhuma atividade registrada no período.${v.ultimoAcesso ? ` Último acesso: ${diaHora(v.ultimoAcesso)}.` : ''}`}</div>
          ) : (
            <ol className="feed-eq">
              {linhas.map(({ a, n }) => {
                const t = tipoDe(a.tipo);
                const titulo = (t.titulo || a.descricao) + (n > 1 ? ` (${n})` : '');
                const det = a.tipo === 'EMAIL_ENVIADO' || a.tipo === 'EMAIL_RECEBIDO' ? (a.dados.assunto ?? '') + (a.dados.trecho && a.tipo === 'EMAIL_RECEBIDO' ? ` — “${a.dados.trecho}”` : '')
                  : t.titulo && a.descricao !== t.titulo ? a.descricao : '';
                return (
                  <li key={a.id}>
                    <time className="num small faint" style={{ minWidth: periodo === '7d' ? 84 : 44 }}>{periodo === '7d' ? diaHora(a.createdAt) : hora(a.createdAt)}</time>
                    <span className={`ico ${t.cls}`}><Icon name={t.icone} size={15} /></span>
                    <div className="col" style={{ gap: 2, minWidth: 0 }}>
                      <span style={{ fontSize: 13.5 }}>
                        <strong style={{ fontWeight: 600 }}>{titulo}</strong>
                        {n === 1 && a.clienteId && a.cliente && <> · <Link to={`/clientes/${a.clienteId}`}>{a.cliente}</Link></>}
                        {a.campanha && <> · <span className="muted">{a.campanha}</span></>}
                      </span>
                      {n === 1 && det && <span className="small muted" style={{ overflowWrap: 'anywhere' }}>{det}</span>}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </aside>
    </div>
  );
}
